import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { BarChart } from '@/charts/BarChart'
import { SERIES } from '@/charts/palette'
import { usePageLoader } from '@/components/LogoLoader'
import { SortableTable, type Column } from '@/components/SortableTable'
import { StatTile } from '@/components/StatTile'
import { fmtEur, fmtNumber, intlLocale, useT } from '@/i18n'
import { crewsMessages } from '@/i18n/messages/crews'
import type { CrewBonus, CrewBonusMonth } from './types'

/**
 * What the crew would earn under the bonus rules configured on the Bonus page, month by month.
 * Same formula as that page (BonusQuery), keyed on the crew rather than the leader's name.
 * A preview: nothing here is paid or written anywhere.
 */
export function BonusTab({ crewId }: { crewId: string }) {
  const t = useT(crewsMessages)
  const { data, isPending, error } = useQuery<CrewBonus>({
    queryKey: ['crew-bonus', crewId],
    queryFn: () => api.get<CrewBonus>(`/api/crews/${crewId}/bonus`),
    staleTime: 5 * 60_000,
  })
  const loader = usePageLoader(isPending, t('loadingBonus'))
  if (loader) return loader
  if (error || !data) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('notFound')}</p>

  const cfg = data.config
  const monthName = new Intl.DateTimeFormat(intlLocale(), { month: 'long', year: 'numeric' })
  const short = new Intl.DateTimeFormat(intlLocale(), { month: 'short' })
  const label = (m: CrewBonusMonth) => monthName.format(new Date(Date.UTC(m.year, m.month - 1, 15)))
  // The current month is still running; headline the last complete one.
  const done = data.months.slice(0, -1)
  const last = [...done].reverse().find((m) => m.days > 0) ?? data.months[data.months.length - 1]!
  const prev = done[done.indexOf(last) - 1]
  const change = prev && prev.earnings > 0 ? ((last.earnings - prev.earnings) / prev.earnings) * 100 : null
  const extras = cfg.mealPerWorkerDay > 0 || cfg.crewLeaderBonus > 0 || cfg.newWorkerBonus > 0

  const columns: Column<CrewBonusMonth>[] = [
    { key: 'month', header: t('bonusMonth'), sortValue: (m) => m.year * 100 + m.month, render: (m) => <span className="whitespace-nowrap">{label(m)}</span> },
    { key: 'days', header: t('colCrewDays'), align: 'right', sortValue: (m) => m.days, render: (m) => <span className="tabular">{m.days}</span> },
    { key: 'metres', header: t('colMetres'), align: 'right', sortValue: (m) => m.metres, render: (m) => <span className="tabular">{fmtNumber(m.metres)}</span> },
    { key: 'hours', header: t('bonusHours'), align: 'right', sortValue: (m) => m.hours, render: (m) => <span className="tabular">{fmtNumber(m.hours)}</span> },
    { key: 'earnings', header: t('bonusEarnings'), align: 'right', sortValue: (m) => m.earnings, render: (m) => <span className="tabular">{fmtEur(m.earnings)}</span> },
    { key: 'perHour', header: t('bonusPerHour'), align: 'right', sortValue: (m) => m.perHour, render: (m) => <span className="tabular">{fmtEur(m.perHour, 2)}</span> },
    {
      key: 'norm', header: t('bonusNorm'), align: 'right', sortValue: (m) => m.normPct,
      render: (m) => <span className={`tabular ${m.normPct >= 1 ? 'text-green-600 dark:text-green-400' : ''}`}>{m.hours > 0 ? `${fmtNumber(m.normPct * 100, 0)}%` : '—'}</span>,
    },
    ...(extras ? [
      { key: 'extras', header: t('bonusExtras'), align: 'right' as const, sortValue: (m: CrewBonusMonth) => m.meal + m.leaderBonus + m.newWorkerBonus,
        render: (m: CrewBonusMonth) => <span className="tabular" title={t('bonusExtrasHint', { meal: fmtEur(m.meal), leader: fmtEur(m.leaderBonus), newWorkers: m.newWorkers, newBonus: fmtEur(m.newWorkerBonus) })}>{fmtEur(m.meal + m.leaderBonus + m.newWorkerBonus)}</span> },
    ] : []),
    { key: 'total', header: t('bonusTotal'), align: 'right', sortValue: (m) => m.total, render: (m) => <span className="tabular font-semibold">{fmtEur(m.total)}</span> },
  ]

  return (
    <div className="space-y-4">
      <p className="text-xs text-neutral-500">
        {t('bonusIntro', { norm: fmtEur(cfg.normPerHour, 2) })}{' '}
        <Link to="/bonus" className="text-brand-700 hover:underline dark:text-brand-100">{t('bonusOpenPage')}</Link>
      </p>

      <h3 className="text-sm font-semibold">{label(last)}</h3>
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <StatTile label={t('bonusEarnings')} value={last.earnings} unit="EUR" {...(change != null ? { changePct: change, compareLabel: t('bonusVsMonthBefore') } : {})} />
        <StatTile label={t('bonusPerHour')} value={last.perHour} unit="EUR" display={fmtEur(last.perHour, 2)} />
        <StatTile label={t('bonusNorm')} value={last.normPct * 100} unit="" display={last.hours > 0 ? `${fmtNumber(last.normPct * 100, 0)}%` : '—'} />
        {extras
          ? <StatTile label={t('bonusTotal')} value={last.total} unit="EUR" />
          : <StatTile label={t('colCrewDays')} value={last.days} unit="" />}
      </div>

      <div className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <div className="flex items-baseline justify-between text-sm">
          <span>{t('bonusNormBar', { norm: fmtEur(cfg.normPerHour, 2) })}</span>
          <span className="tabular font-semibold">{last.hours > 0 ? `${fmtNumber(last.normPct * 100, 0)}%` : '—'}</span>
        </div>
        <div className="mt-2 h-2.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
          <div className="h-full rounded-full"
            style={{ width: `${Math.min(last.normPct * 100, 100)}%`, backgroundColor: last.normPct >= 1 ? 'var(--viz-good)' : last.normPct >= 0.7 ? 'var(--viz-warning)' : 'var(--viz-serious)' }} />
        </div>
      </div>

      <BarChart
        title={t('bonusChartTitle')}
        subtitle={t('bonusChartSubtitle')}
        unit="EUR"
        color={SERIES.money}
        bars={data.months.map((m) => ({ label: short.format(new Date(Date.UTC(m.year, m.month - 1, 15))), value: Number(m.total) }))}
      />

      <SortableTable columns={columns} rows={[...data.months].reverse()} rowKey={(m) => `${m.year}-${m.month}`} />

      <p className="text-xs text-neutral-400">
        {t('bonusAssumptions', { window: cfg.newWorkerWindowDays })}
      </p>
    </div>
  )
}
